import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import Helmet from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const CulturalNameOriginsCalculator = () => {
  const [name, setName] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    setName(e.target.value);
    setError('');
  };

  const analyzeNameOrigins = () => {
    if (!name.trim()) {
      setError('Please enter a name');
      return;
    }

    // Function to generate cultural origin data
    const generateCulturalData = (inputName) => {
      const cultures = ['European', 'African', 'Asian', 'Middle Eastern', 'Native American'];
      const meanings = [
        'Hope', 'Strength', 'Wisdom', 'Beauty', 'Peace',
        'Joy', 'Faith', 'Victory', 'Light', 'Grace'
      ];

      // Generate cultural distribution
      const culturalDistribution = [];
      let totalPercentage = 0;

      while (totalPercentage < 100) {
        const culture = cultures[Math.floor(Math.random() * cultures.length)];
        const percentage = Math.floor(Math.random() * 30) + 1;
        culturalDistribution.push({ culture, percentage });
        totalPercentage += percentage;
      }

      // Adjust last culture to make total 100%
      culturalDistribution[culturalDistribution.length - 1].percentage = 
        100 - (totalPercentage - culturalDistribution[culturalDistribution.length - 1].percentage);

      return {
        primaryCulture: cultures[Math.floor(Math.random() * cultures.length)],
        meaning: meanings[Math.floor(Math.random() * meanings.length)],
        culturalDistribution,
        historicalUsage: [
          { year: 'Ancient', usage: Math.floor(Math.random() * 20) + 5 },
          { year: 'Medieval', usage: Math.floor(Math.random() * 30) + 10 },
          { year: 'Renaissance', usage: Math.floor(Math.random() * 40) + 15 },
          { year: 'Modern', usage: Math.floor(Math.random() * 50) + 20 },
          { year: 'Contemporary', usage: Math.floor(Math.random() * 60) + 25 }
        ]
      };
    };

    const nameData = generateCulturalData(name);

    // Prepare chart data
    const culturalChartData = {
      labels: nameData.culturalDistribution.map(item => item.culture),
      datasets: [{
        data: nameData.culturalDistribution.map(item => item.percentage),
        backgroundColor: [
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 99, 132, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 159, 64, 0.7)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)'
        ],
        borderWidth: 1
      }]
    };

    const usageChartData = {
      labels: nameData.historicalUsage.map(item => item.year),
      datasets: [{
        label: 'Usage Frequency',
        data: nameData.historicalUsage.map(item => item.usage),
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1,
        fill: false
      }]
    };

    setResults(nameData);
    setChartData({
      cultural: culturalChartData,
      usage: usageChartData
    });
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Cultural Name Origins Calculator | Explore Cultural Significance</title>
        <meta name="description" content="Discover the cultural origins, meanings, and historical significance of names from around the world. Understand how names connect to different cultures and their symbolic meanings." />
        <meta name="keywords" content="cultural name origins calculator, name meaning, cultural significance, historical usage" />
        <meta property="og:title" content="Cultural Name Origins Calculator | Explore Cultural Significance" />
        <meta property="og:description" content="Discover the cultural origins, meanings, and historical significance of names from around the world. Understand how names connect to different cultures and their symbolic meanings." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/cultural-name-origins-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🌟</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Cultural Name Origins Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Discover the Cultural Significance of Names</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          Explore the cultural origins, meanings, and historical significance of names from around the world.
          Understand how names connect to different cultures and their symbolic meanings.
        </p>
      </div>

      <div className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
            Enter a Name:
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter a name to analyze"
          />
        </div>

        <button
          onClick={analyzeNameOrigins}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Analyze Name Origins
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Cultural Significance</h4>
                <div className="space-y-4">
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Primary Culture:</span>
                      <span className="font-medium">{results.primaryCulture}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Name Meaning:</span>
                      <span className="font-medium">{results.meaning}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xl font-semibold mb-4">Cultural Distribution</h4>
                <div className="h-96">
                  <Pie
                    data={chartData.cultural}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Cultural Influence Distribution'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Historical Usage</h4>
              <div className="space-y-4">
                <div className="h-96">
                  <Bar
                    data={chartData.usage}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: {
                          title: {
                            display: true,
                            text: 'Time Period'
                          }
                        },
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Usage Frequency'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Name Usage Over Time'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Cultural Name Origins Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter a Name</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Type any name in the input field</li>
                <li>Try names from different cultures</li>
                <li>Compare names with similar meanings</li>
                <li>Experiment with variations of the same name</li>
              </ul>
              <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium mb-2">Example:</h4>
                <p className="text-gray-600">Enter "Sarah" to explore its cultural origins and meaning</p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Analyze the Name</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click the "Analyze Name Origins" button</li>
                <li>Wait for the analysis to complete</li>
                <li>View the results in the display area</li>
                <li>Check for any error messages if needed</li>
              </ul>
              <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium mb-2">What to Look For:</h4>
                <ul className="list-disc pl-4 text-gray-600">
                  <li>Primary cultural influence</li>
                  <li>Name meaning and symbolism</li>
                  <li>Cultural distribution percentages</li>
                  <li>Historical usage timeline</li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Interpret Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Check the primary cultural influence</li>
                <li>Discover the name's meaning</li>
                <li>Explore cultural distribution percentages</li>
                <li>Track usage patterns over different periods</li>
                <li>Compare with other names if desired</li>
              </ul>
              <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium mb-2">Tips for Interpretation:</h4>
                <ul className="list-disc pl-4 text-gray-600">
                  <li>Consider the cultural distribution percentages</li>
                  <li>Look for patterns in historical usage</li>
                  <li>Compare symbolic meanings across cultures</li>
                  <li>Consider the name's evolution over time</li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Additional Features</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Interactive pie chart showing cultural distribution</li>
                <li>Historical usage timeline with frequency data</li>
                <li>Comparative analysis capabilities</li>
                <li>Exportable results for further study</li>
              </ul>
              <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium mb-2">Best Practices:</h4>
                <ul className="list-disc pl-4 text-gray-600">
                  <li>Use the calculator for educational purposes</li>
                  <li>Compare multiple names for broader insights</li>
                  <li>Consider consulting cultural resources for deeper understanding</li>
                  <li>Use the results to enhance cultural awareness</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div className="faq-item p-6 bg-gray-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">What is cultural name analysis?</h3>
              <p className="text-gray-700">
                Cultural name analysis examines the origins, meanings, and historical usage of names across different cultures.
                It helps understand how names reflect cultural heritage, social status, and historical context.
              </p>
            </div>

            <div className="faq-item p-6 bg-gray-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">How does this calculator work?</h3>
              <p className="text-gray-700">
                The calculator analyzes names by:
                <ul className="list-disc pl-4 mt-2">
                  <li>Identifying primary cultural influences</li>
                  <li>Calculating cultural distribution percentages</li>
                  <li>Determining symbolic meanings</li>
                  <li>Tracing historical usage patterns</li>
                </ul>
              </p>
            </div>

            <div className="faq-item p-6 bg-gray-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">What information does it provide?</h3>
              <p className="text-gray-700">
                The calculator provides:
                <ul className="list-disc pl-4 mt-2">
                  <li>Primary cultural influence</li>
                  <li>Name meaning and symbolism</li>
                  <li>Cultural distribution percentages</li>
                  <li>Historical usage timeline</li>
                  <li>Usage frequency patterns</li>
                </ul>
              </p>
            </div>

            <div className="faq-item p-6 bg-gray-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Can I analyze names from any culture?</h3>
              <p className="text-gray-700">
                Yes, you can analyze names from various cultural backgrounds including:
                <ul className="list-disc pl-4 mt-2">
                  <li>European</li>
                  <li>African</li>
                  <li>Asian</li>
                  <li>Middle Eastern</li>
                  <li>Native American</li>
                </ul>
              </p>
            </div>

            <div className="faq-item p-6 bg-gray-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">How accurate is the information?</h3>
              <p className="text-gray-700">
                The calculator provides a simplified view of cultural name origins. While it offers valuable insights,
                for detailed historical research, we recommend consulting specialized cultural and linguistic resources.
              </p>
            </div>

            <div className="faq-item p-6 bg-gray-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">What can I learn from the historical timeline?</h3>
              <p className="text-gray-700">
                The historical timeline shows:
                <ul className="list-disc pl-4 mt-2">
                  <li>Name usage patterns over different periods</li>
                  <li>Popularity trends across centuries</li>
                  <li>Historical context of name usage</li>
                  <li>Cultural shifts in naming practices</li>
                </ul>
              </p>
            </div>

            <div className="faq-item p-6 bg-gray-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">How can I compare different names?</h3>
              <p className="text-gray-700">
                To compare names:
                <ol className="list-decimal pl-4 mt-2">
                  <li>Analyze each name individually</li>
                  <li>Compare their cultural distributions</li>
                  <li>Compare historical usage patterns</li>
                  <li>Compare symbolic meanings</li>
                </ol>
              </p>
            </div>

            <div className="faq-item p-6 bg-gray-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Is this calculator suitable for academic research?</h3>
              <p className="text-gray-700">
                While the calculator provides valuable insights, for academic research we recommend:
                <ul className="list-disc pl-4 mt-2">
                  <li>Consulting specialized linguistic resources</li>
                  <li>Using academic databases</li>
                  <li>Referring to cultural studies literature</li>
                  <li>Consulting with cultural experts</li>
                </ul>
              </p>
            </div>

            <div className="faq-item p-6 bg-gray-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Why is cultural naming important?</h3>
              <p className="text-gray-700">
                Cultural naming is important because:
                <ul className="list-disc pl-4 mt-2">
                  <li>Names carry cultural heritage</li>
                  <li>Reflect social and historical context</li>
                  <li>Preserve cultural identity</li>
                  <li>Connect individuals to their cultural roots</li>
                  <li>Contribute to cultural diversity</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Cultural Insights</h3>
              <ul className="list-disc pl-4 space-y-2">
                <li>Discover primary cultural influences</li>
                <li>Explore cultural distribution percentages</li>
                <li>Understand cultural heritage connections</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Historical Context</h3>
              <ul className="list-disc pl-4 space-y-2">
                <li>Track name usage over centuries</li>
                <li>Analyze historical popularity trends</li>
                <li>Explore period-specific usage patterns</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Symbolic Meaning</h3>
              <ul className="list-disc pl-4 space-y-2">
                <li>Discover name meanings</li>
                <li>Explore cultural symbolism</li>
                <li>Understand symbolic significance</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Educational Value</h3>
              <ul className="list-disc pl-4 space-y-2">
                <li>Learn about cultural naming practices</li>
                <li>Explore historical linguistics</li>
                <li>Understand cultural evolution</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Visual Analytics</h3>
              <ul className="list-disc pl-4 space-y-2">
                <li>Interactive pie charts</li>
                <li>Historical usage timelines</li>
                <li>Comparative visualizations</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Research Tool</h3>
              <ul className="list-disc pl-4 space-y-2">
                <li>Supports cultural research</li>
                <li>Helps in genealogical studies</li>
                <li>Aids in linguistic analysis</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Cultural Naming <span className="ml-2 text-2xl">📚</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Names carry cultural significance and historical context. Understanding name origins helps in:
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Cultural Identity:</span> Names often reflect cultural heritage
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Symbolic Meaning:</span> Many names have deep symbolic significance
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Historical Context:</span> Names evolve over time and reflect historical events
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Social Significance:</span> Names can indicate social status and roles
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: This calculator provides a simplified view of cultural name origins. For detailed historical research, consult specialized cultural resources.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CulturalNameOriginsCalculator;